import { from, Observable } from "rxjs";
import { PageRequest } from "src/app/support/page-request";
import { Paginated } from "src/app/support/paginated";
import { find } from "lodash-es";
import { rxRandomErrorDelayAsync } from "src/app/support/rx-random-error-delay";
import { map, switchMap } from "rxjs/operators";
import { UserNotify } from "src/app/support/notify-decorator";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { uuid, UUID } from "src/app/support/uuid";

import {
  loadDemoStorage,
  writeDemoStorage
} from "src/app/support/demo-storage";
import { Medication } from "../models/medications/medications";
import { MedicationsLoad } from "../models/medications/ medications.load";
import { MedicationCreate } from "../models/medications/medications.create";
import { MedicationsDelete } from "../models/medications/medications.delete";
import { MedicationsUpdate } from "../models/medications/medications.update";
import { MedicationsBackend } from "./medications-backend";

const DB_NAME = "medication";

export class MedicationsMockBackend extends MedicationsBackend {
  load(request: MedicationsLoad): Observable<Medication> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return find(list, (x) => x.uuid === request.uuid)!;
      })
    );
  }

  list(request: PageRequest): Observable<Paginated<Medication>> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return <Paginated<Medication>>{
          list,
          count: list.length
        };
      })
    );
  }

  @UserNotify({
    successMessage: marker("Nuovo farmaco creato"),
    errorMessage: marker("Impossibile creare il farmaco")
  })
  create(request: MedicationCreate): Observable<UUID> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        const newUuid = uuid();

        const newMedication: Partial<Medication> = {
          uuid: newUuid,
          ...request
        };

        return from(writeDemoStorage(DB_NAME, [...list, newMedication])).pipe(
          map(() => newUuid)
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco aggiornato"),
    errorMessage: marker("Impossibile aggiornare il farmaco")
  })
  update(request: MedicationsUpdate): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.map((x) => {
              if (x.uuid === request.uuid) {
                return {
                  ...x,
                  ...request
                };
              }
              return x;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco rimosso"),
    errorMessage: marker("Impossibile rimuovere il farmaco")
  })
  delete(request: MedicationsDelete): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.filter((x) => x.uuid !== request.uuid)
          )
        );
      })
    );
  }
}

async function loadDb(): Promise<Medication[]> {
  return (await loadDemoStorage<Medication[]>(DB_NAME)) || [];
}
